"use client";

import { cn } from "@devhop/ui";
import {
  Node,
  NodeViewWrapper,
  ReactNodeViewRenderer,
  mergeAttributes,
  type CommandProps,
  type Editor,
  type NodeViewProps,
} from "@tiptap/react";
import { config } from "../lib/config";
import { PresignedFileUpload } from "../ui/PresignedFileUpload";

const NODE_HANDLES_SELECTED_STYLE_CLASSNAME = "node-handles-selected-style";

export interface ImagePlaceholderOptions {
  HTMLAttributes: Record<string, unknown>;
  onDrop: (files: File[], editor: Editor) => void;
  onDropRejected?: (files: File[], editor: Editor) => void;
  onEmbed: (url: string, editor: Editor) => void;
  allowedMimeTypes?: Record<string, string[]>;
  maxFiles?: number;
  maxSize?: number;
}

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    imagePlaceholder: {
      /**
       * Inserts an image placeholder
       */
      insertImagePlaceholder: () => ReturnType;
    };
  }
}

export const ImagePlaceholder = Node.create<ImagePlaceholderOptions>({
  name: "image-placeholder",

  addOptions() {
    return {
      HTMLAttributes: {},
      onDrop: () => {},
      onDropRejected: () => {},
      onEmbed: () => {},
    };
  },

  group: "block",

  parseHTML() {
    return [{ tag: `div[data-type="${this.name}"]` }];
  },

  renderHTML({ HTMLAttributes }) {
    return ["div", mergeAttributes(HTMLAttributes)];
  },

  addNodeView() {
    return ReactNodeViewRenderer(ImagePlaceholderComponent, {
      className: NODE_HANDLES_SELECTED_STYLE_CLASSNAME,
    });
  },

  addCommands() {
    return {
      insertImagePlaceholder: () => (props: CommandProps) => {
        return props.commands.insertContent({
          type: "image-placeholder",
        });
      },
    };
  },
});

function ImagePlaceholderComponent(props: NodeViewProps) {
  const { editor, selected } = props;

  const handleUploadSuccess = (key: string) => {
    const imageUrl = key.startsWith("http")
      ? key
      : `${config.apiUrl}/files/${key}`;
    editor.chain().focus().setImage({ src: imageUrl }).run();
  };

  return (
    <NodeViewWrapper className="w-full">
      <div
        className={cn(
          "rounded-md bg-accent p-2 py-3",
          selected && "bg-primary/10",
        )}
      >
        <PresignedFileUpload
          keyPrefix="uploads/editor"
          accept="image/*"
          onSuccess={handleUploadSuccess}
          buttonLabel="ເພີ່ມຮູບພາບ"
        />
      </div>
    </NodeViewWrapper>
  );
}
